import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface TimeSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  interval?: number; // interval in minutes
  start?: string; // HH:mm (24h format)
  end?: string; // HH:mm (24h format)
  placeholder?: string;
  className?: string;
}

function generateTimes(interval: number, start: string, end: string): { value: string; label: string }[] {
  const times: { value: string; label: string }[] = [];
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  let totalMinutesStart = startH * 60 + startM;
  const totalMinutesEnd = endH * 60 + endM;

  while (totalMinutesStart <= totalMinutesEnd) {
    const h = Math.floor(totalMinutesStart / 60);
    const m = totalMinutesStart % 60;
    const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    const ampmH = ((h + 11) % 12) + 1; // convert 0-23 to 1-12
    const period = h >= 12 ? 'PM' : 'AM';
    const label = `${ampmH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${period}`;
    times.push({ value, label });
    totalMinutesStart += interval;
  }
  return times;
}

export const TimeSelect: React.FC<TimeSelectProps> = ({
  value,
  onValueChange,
  interval = 30,
  start = '07:00',
  end = '21:00',
  placeholder = 'Select Time',
  className,
}) => {
  const timeOptions = generateTimes(interval, start, end);

  // Find the label for the selected value
  const selectedOption = timeOptions.find(opt => opt.value === value);
  const displayValue = value && selectedOption ? selectedOption.label : '';

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={cn('w-full', className)}>
        <SelectValue placeholder={placeholder}>
          {displayValue}
        </SelectValue>
      </SelectTrigger>
      <SelectContent position="popper" align="start" className="max-h-60 overflow-y-auto">
        {timeOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}; 